import { createSelector } from 'reselect';

import type { CustomInterrogationListItem } from '@frontend/api';

import type { RootState } from 'store/configureStore';

import { types, type GeometricInterrogationAction } from './actions';
import type {
  InterrogationInfo,
  InterrogationMaterialQuery,
  InterrogationSelection,
} from './types';

type GeometricInterrogationState = {
  isEditingInterrogationSetup: boolean;
  isFetching: boolean;
  query: {
    material: InterrogationMaterialQuery | null;
    task: string | null;
  };
  /** Live run state per part file; absent once a run succeeds. */
  runStatusByPartFileUuid: Record<string, 'running' | 'failed'>;
  searchResults: CustomInterrogationListItem[];
  selectedResult: InterrogationSelection | null;
};

const initialState: GeometricInterrogationState = {
  isEditingInterrogationSetup: false,
  isFetching: false,
  query: {
    material: null,
    task: null,
  },
  runStatusByPartFileUuid: {},
  searchResults: [],
  selectedResult: null,
};

const reducer = (
  state: GeometricInterrogationState = initialState,
  action: GeometricInterrogationAction,
): GeometricInterrogationState => {
  switch (action.type) {
    case types.CANCEL_EDIT_INTERROGATION_SETUP:
      return {
        ...state,
        isEditingInterrogationSetup: false,
      };
    case types.CLEAR_INTERROGATION:
      return {
        ...state,
        query: initialState.query,
        searchResults: [],
        selectedResult: null,
      };
    case types.EDIT_INTERROGATION_SETUP:
      return {
        ...state,
        isEditingInterrogationSetup: true,
      };
    case types.RECEIVE_QUERY: {
      const { material, task } = action.payload;
      return {
        ...state,
        isFetching: true,
        query: {
          material,
          task,
        },
      };
    }
    case types.RECEIVE_RUN_STATUS: {
      const { partFileUuid, status } = action.payload;
      const { [partFileUuid]: _previous, ...rest } =
        state.runStatusByPartFileUuid;
      return {
        ...state,
        runStatusByPartFileUuid:
          status === 'succeeded' ? rest : { ...rest, [partFileUuid]: status },
      };
    }
    case types.SELECT_INTERROGATION: {
      const { result: selectedResult } = action.payload;
      return {
        ...state,
        selectedResult,
      };
    }
    case types.RECEIVE_SEARCH_RESULTS: {
      const { results: searchResults } = action.payload;
      return {
        ...state,
        isFetching: false,
        searchResults,
      };
    }
    case types.SUBMIT_INTERROGATION: {
      const { result: selectedResult } = action.payload;
      return {
        ...state,
        isEditingInterrogationSetup: false,
        selectedResult,
      };
    }
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): GeometricInterrogationState =>
  state.modules.viewerPage.app.geometricInterrogation;

export const getIsEditingInterrogationSetup = (state: RootState) =>
  getScope(state).isEditingInterrogationSetup;

export const getIsFetching = (state: RootState) => getScope(state).isFetching;

export const getQuery = (state: RootState) => getScope(state).query;

export const getSearchResults = (state: RootState) =>
  getScope(state).searchResults;

export const getSelectedResult = (state: RootState) =>
  getScope(state).selectedResult;

/** Stable empty projection. Readers memoize on this object's identity. */
export const EMPTY_INTERROGATION_INFO: InterrogationInfo = Object.freeze({});

export const getInterrogationInfo = createSelector(
  [getSelectedResult],
  (selected): InterrogationInfo =>
    selected
      ? {
          documentHash: selected.documentHash,
          interrogationFileId: selected.interrogationFileId,
          task: selected.task,
        }
      : EMPTY_INTERROGATION_INFO,
);

export const getIsInterrogationRunInProgress = (
  state: RootState,
  partFileUuid: string | null,
) =>
  partFileUuid != null &&
  getScope(state).runStatusByPartFileUuid[partFileUuid] === 'running';

export const getHasInterrogationRunFailed = (
  state: RootState,
  partFileUuid: string | null,
) =>
  partFileUuid != null &&
  getScope(state).runStatusByPartFileUuid[partFileUuid] === 'failed';
